import React, { useState } from 'react'

const Leadership = () => {
  const [activeBio, setActiveBio] = useState(null)

  // Data is now separated into distinct groups for clarity and structure.
  const boardOfDirectors = [
    {
      id: 1,
      name: 'Leighton Jacobus',
      title: 'Founder & Executive Director',
      shortBio:
        'A multifaceted leader who combines fiscal sponsorship expertise with nonprofit innovation and a passion for horticulture.',
      fullBio:
        "Leighton's journey is anything but conventional. From building successful businesses to pursuing medical school and getting published in botany, he's always followed his curiosity. As a recognized expert in fiscal sponsorship, he's helped scale some of the nation's largest fiscal sponsoring organizations before founding Cause and Impact. When he's not revolutionizing nonprofit operations, you'll find him tending to his extensive plant collection or hiking with his three dogs - a German Shepherd and two German Shorthaired Pointers. His unique blend of business acumen, scientific background, and genuine passion for helping others makes him the driving force behind our organization's innovative approach to fiscal sponsorship.",
      image: '/assets/staffimages/Leighton-Jacobus-Staffimage.png',
      expertise: [
        'Fiscal Sponsorship',
        'Nonprofit Growth',
        'Compliance',
        'Operations',
        'Botanical Research',
      ],
      linkedin: 'https://linkedin.com/in/leightonjacobus',
    },
    {
      id: 3,
      name: 'Simon Izaguirre, MD',
      title: 'Director & Board Certified Vascular Neurologist',
      shortBio:
        'A renaissance man combining medical innovation with engineering passion.',
      fullBio:
        "Dr. Simon isn't your typical neurologist. When he's not developing cutting-edge approaches to stroke treatment, you might find him in his garage engineering race car parts or crafting his latest home-brewed beer recipe. His innovative spirit extends from the medical field to the mountains of Colorado, where he's an avid hiker. Simon and Leighton's friendship began in medical school, where they discovered their shared passion for pushing boundaries and thinking differently. This led to Simon becoming one of our founding directors, bringing his unique perspective to our mission and a pursuit of excellence. His ability to excel in everything from neurology to engineering embodies our organization's belief that the best solutions come from thinking beyond traditional boundaries.",
      image: '/assets/staffimages/SimonIzaguirreTeamPhoto.png',
      expertise: [
        'Vascular Neurology',
        'Medical Innovation',
        'Engineering',
        'Stroke Prevention',
        'Leadership',
      ],
      linkedin: '#', // Add actual link when available
    },
    {
      id: 4,
      name: 'Kirsty Zapatka',
      title: 'Director, Treasurer',
      shortBio:
        'A financial strategist who makes nonprofit accounting both transparent and transformative.',
      fullBio:
        "Kirsty brings clarity and innovation to the complex world of nonprofit finance. As our Treasurer, she ensures our financial operations are not just compliant, but strategic and forward-thinking. Her approach to nonprofit accounting goes beyond numbers – it's about creating sustainable impact through smart financial management. With her expertise, we're transforming how organizations think about their financial health while maintaining the highest standards of fiscal responsibility and transparency.",
      image: '/assets/staffimages/Kirsty-Zapatka-Staffimage.png',
      expertise: [
        'Nonprofit Accounting',
        'Financial Strategy',
        'Tax Compliance',
        'Treasury Management',
        'Risk Assessment',
      ],
      linkedin: 'https://www.linkedin.com/in/kirsty-white-aa83a56b/',
    },
  ]

  const advisorsAndContributors = [
    {
      id: 6,
      name: 'Peter Shay',
      title: 'Systems & Salesforce Operations',
      shortBio:
        'Peter drives the design and implementation of backend core systems, ensuring Cause and Impact’s technology and operations are built to scale.',
      fullBio:
        'Peter drives the design and implementation of our backend core systems, ensuring Cause and Impact’s technology and operations are built to scale. His expertise in Salesforce and systems architecture is foundational to our ability to provide seamless, efficient fiscal sponsorship services to our projects.',
      image: '/assets/staffimages/PeterShay-Staffimage.png',
      expertise: [
        'Systems Architecture',
        'Salesforce',
        'Operations',
        'Scalability',
        'Technology',
      ],
      linkedin: 'https://www.linkedin.com/in/petershay/',
    },
    {
      id: 7,
      name: 'Amy Fox',
      title: 'Finance',
      shortBio:
        'Amy supports the financial backbone of our organization, helping ensure accountability, clarity, and smart resource management as we grow.',
      fullBio:
        'Amy supports the financial backbone of our organization, helping ensure accountability, clarity, and smart resource management as we grow. Her contributions are vital to maintaining the fiscal health and transparency that our partners and donors expect.',
      image: '/assets/staffimages/AmyFox-Staffimage.png',
      expertise: [
        'Financial Systems',
        'Accountability',
        'Resource Management',
        'Nonprofit Bookkeeping',
        'Operations Support',
      ],
      linkedin: '#', // Add actual link when available
    },
  ]

  const supportAndInnovation = [
    {
      id: 5,
      name: 'PHIL',
      title: 'AI Compliance Expert',
      shortBio:
        'Phil is your AI-powered compliance guide—helping nonprofits and fiscally sponsored projects navigate IRS rules, grant requirements, and nonprofit regulations with clarity and confidence.',
      fullBio:
        'Phil isn’t just any chatbot—he’s the first AI built specifically to help nonprofits and fiscally sponsored projects navigate compliance with confidence. Developed by SponsFI and assigned to Cause and Impact, Inc., Phil serves as the first line of support for nonprofit leaders, fiscal sponsors, and project managers who need clear, reliable guidance on nonprofit operations and IRS regulations. As a Proactive Helper for IRS & Legal Compliance (P.H.I.L.), he’s more than just a virtual assistant—he’s an on-demand compliance guide, best-practice resource, and fiscal sponsorship expert, all in one. While Phil isn’t a lawyer or CPA, he is trained extensively in 501(c)(3) compliance, fiscal sponsorship best practices, nonprofit governance, donor stewardship, and state and federal nonprofit regulations. His knowledge extends beyond industry standards—he’s also fluent in Cause and Impact’s specific policies, services, and FAQs, making him an invaluable resource for the organizations we support. Phil is available 24/7 to answer questions, provide best-practice guidance, and help organizations stay compliant, avoid mistakes, and focus on their mission.',
      image: '/assets/staffimages/PHIL-Staffimage.png',
      expertise: [
        'AI Compliance Expert',
        'Nonprofit Guidance',
        'Fiscal Sponsorship Assistant',
        'IRS Regulations',
        'Risk Assessment',
      ],
      linkedin: 'https://www.linkedin.com/company/justaskphil',
    },
  ]

  // A reusable component to render a grid of leaders for a specific section
  const LeaderGrid = ({ leaders }) => (
    <div className="leaders-grid">
      {leaders.map((leader, index) => (
        <div
          key={leader.id}
          className={`leader-card ${activeBio === leader.id ? 'active' : ''}`}
          onClick={() => setActiveBio(activeBio === leader.id ? null : leader.id)}
          style={{ '--card-index': index }}
        >
          <div className="leader-image">
            <img src={leader.image} alt={leader.name} />
            <div className="leader-overlay">
              <span>Click to learn more</span>
            </div>
          </div>
          <div className="leader-info">
            <h2>{leader.name}</h2>
            <h3>{leader.title}</h3>
            <p>{activeBio === leader.id ? leader.fullBio : leader.shortBio}</p>
            {activeBio === leader.id && (
              <div className="leader-details">
                <div className="expertise-tags">
                  {leader.expertise.map((skill, i) => (
                    <span key={i} className="expertise-tag">
                      {skill}
                    </span>
                  ))}
                </div>
                {leader.linkedin && leader.linkedin !== '#' && (
                  <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="linkedin-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Connect on LinkedIn
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <>
    <style>{`
        .leadership-wrapper {
            min-height: 100vh;
            background: linear-gradient(135deg, #2563eb, #059669);
            padding: 120px 20px 60px;
            font-family: sans-serif;
        }

        .leadership-container {
            max-width: 1400px;
            margin: 0 auto;
        }

        .leadership-header {
            text-align: center;
            margin-bottom: 60px;
            color: #fff;
        }

        .leadership-header h1 {
            font-size: 3rem;
            font-weight: 700;
            margin-bottom: 16px;
            background: linear-gradient(135deg, #fff, #e2e8f0);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
        }

        .leadership-header p {
            font-size: 1.2rem;
            color: rgba(255, 255, 255, 0.9);
        }
        
        .leadership-section {
          padding-top: 60px;
          margin-bottom: 20px;
        }

              .section-header {
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center !important;
        }

        .section-header h2 {
           font-size: 2.5rem;
          font-weight: 600;
          color: #FFFFFF;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          text-align: center !important;
        }


        .section-intro {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: -20px auto 40px auto;
          text-align: center;
          line-height: 1.6;
        }

        .leaders-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 24px;
            padding: 20px;
            max-width: 1400px;
            margin: 0 auto;
        }

        .leader-card {
            height: 100%;
            display: flex;
            flex-direction: column;
            background: rgba(17, 24, 39, 0.95);
            border-radius: 16px;
            overflow: hidden;
            transition: all 0.3s ease;
            cursor: pointer;
            border: 1px solid rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            position: relative;
            padding-top: 20px;
            text-align: center;
        }

        .leader-card::before {
            display: none;
        }

        .leader-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
        }

        .leader-card:hover::before {
            opacity: 1;
            height: 6px;
        }

        .leader-image {
            position: relative;
            width: 100%;
            padding-top: 100%;
            overflow: hidden;
            border-radius: 50%;
            margin: 20px auto;
            width: 250px;
            height: 250px;
            padding-top: 0;
        }

        .leader-image img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: all 0.3s ease;
            border-radius: 50%;
        }

        .leader-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(37, 99, 235, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: all 0.3s ease;
            border-radius: 50%;
        }

        .leader-card:hover .leader-overlay {
            opacity: 1;
        }

        .leader-overlay span {
            color: #fff;
            font-size: 1.1rem;
            font-weight: 500;
            padding: 8px 16px;
            border-radius: 20px;
            background: rgba(0, 0, 0, 0.5);
        }

        .leader-info {
            padding: 24px;
            color: #fff;
            position: relative;
        }

        .leader-info::before {
            content: '';
            position: absolute;
            top: 1.8rem;
            left: 10%;
            right: 10%;
            height: 4px;
            background: linear-gradient(90deg, #2563eb, #059669);
            opacity: 0.8;
            transition: all 0.3s ease;
        }

        .leader-info h2 {
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 8px;
        }

        .leader-info h3 {
            font-size: 1rem;
            color: #818cf8;
            margin-bottom: 16px;
        }

        .leader-info p {
            font-size: 0.95rem;
            line-height: 1.6;
            color: #e2e8f0;
            margin-bottom: 20px;
        }

        .leader-details {
            opacity: 0;
            transform: translateY(20px);
            transition: all 0.3s ease;
        }

        .leader-card.active .leader-details {
            opacity: 1;
            transform: translateY(0);
        }

        .leader-card.active::before {
            opacity: 1;
            height: 6px;
        }

        .expertise-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 20px;
        }

        .expertise-tag {
            background: rgba(129, 140, 248, 0.1);
            color: #818cf8;
            padding: 4px 10px;
            border-radius: 16px;
            font-size: 0.85rem;
            transition: all 0.2s ease;
        }

        .expertise-tag:hover {
            background: rgba(129, 140, 248, 0.2);
        }

        .linkedin-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: #fff;
            text-decoration: none;
            padding: 8px 16px;
            border-radius: 8px;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            transition: all 0.2s ease;
        }

        .linkedin-link:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }

        /* Animation for expanded state */
        .leader-card.active {
            grid-column: span 4;
            display: grid;
            grid-template-columns: minmax(250px, 1fr) 3fr;
            gap: 24px;
            height: auto;
        }

        @media (max-width: 1400px) {
            .leaders-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 32px;
                padding: 20px;
            }
            
            .leader-card.active {
                grid-column: span 2;
                grid-template-columns: minmax(200px, 1fr) 2fr;
            }
        }

        @media (max-width: 768px) {
            .leaders-grid {
                grid-template-columns: 1fr;
                gap: 24px;
            }

            .leader-card.active {
                grid-column: span 1;
                grid-template-columns: 1fr;
            }
        }

        /* Smooth animations */
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .leader-card {
            animation: fadeIn 0.5s ease forwards;
            animation-delay: calc(0.1s * var(--card-index));
        } 
    `}</style>
    <div className="leadership-wrapper">
      <div className="leadership-container">
        <div className="leadership-header">
          <h1>Passion behind the Cause</h1>
          <p>
            Innovators at the intersection of technology and philanthropy,
            guiding our mission to modernize fiscal sponsorship
          </p>
        </div>

        {/* Board of Directors Section */}
        <div className="leadership-section">
          <div className="section-header">
            <h2>Board of Directors</h2>
          </div>
          <LeaderGrid leaders={boardOfDirectors} />
        </div>

        {/* Advisors & Key Contributors Section */}
        <div className="leadership-section">
          <div className="section-header">
            <h2>Advisors & Key Contributors</h2>
          </div>
          <p className="section-intro">
          Beyond our Board of Directors, Cause and Impact is powered by a dedicated group of collaborators whose expertise in systems, finance, and operations strengthens our foundation. These trusted partners play an integral role in driving our daily work and advancing our mission.
          </p>
          <LeaderGrid leaders={advisorsAndContributors} />
        </div>

        {/* Support & Innovation Section */}
        <div className="leadership-section">
          <div className="section-header">
            <h2>Support & Innovation</h2>
          </div>
          <LeaderGrid leaders={supportAndInnovation} />
        </div>
      </div>
    </div>
    </>
  )
}

export default Leadership
